import React, { useMemo, useRef } from 'react';
import { Handle, Position, NodeProps, useStore } from 'reactflow';
import { PowerOff, Plus } from 'lucide-react';
import { getNodeDefinition as getNodeDef } from '@/utils/nodeDefinitions';
import { getNodeDefinition } from '@/lib/node-registry';
import { useWorkflowContext } from '@/contexts/WorkflowContext';
import { useWorkflowStore } from '@/stores/workflow-store';
import { NodeIcon } from '@/components/ui/node-icon';
import { NodeContextMenu } from '@/components/workflow/NodeContextMenu';
import { workflowColors } from '@/lib/workflow-colors';

interface GenericNodeData {
  label?: string;
  isExecuting?: boolean;
  executionStatus?: 'idle' | 'running' | 'success' | 'failed';
  [key: string]: any;
}

export default function GenericNode({ data, type, id }: NodeProps<GenericNodeData>) {
  const nodeType = type || 'default';
  const { onAddNodeClick, onOpenChatTest } = useWorkflowContext();
  
  // Subscribe to edges changes efficiently using useStore
  const edges = useStore((state) => state.edges);
  
  // Check if a connection is being dragged from a tools or pollSource handle
  const connectionHandleId = useStore((state) => (state as any).connectionHandleId);
  const isDraggingFromToolsHandle = connectionHandleId === 'tools';
  const isDraggingFromPollSourceHandle = connectionHandleId === 'pollSource';

  // Check if this node is a drop target (for drop-on-node feature)
  const highlightedNodeId = useWorkflowStore((state) => state.highlightedNodeId);
  const isDropTarget = highlightedNodeId === id;
  
  // Get node element to calculate screen position
  const nodeRef = useRef<HTMLDivElement>(null);
  
  // Get the full definition from shared/nodes (includes inputs/outputs)
  const fullDefinition = getNodeDefinition(nodeType);
  
  // Helper function to check if an anchor is connected - memoized for performance
  const isAnchorConnected = useMemo(() => {
    return (handleId: string, handleType: 'source' | 'target' = 'source'): boolean => {
      const connected = edges.some(edge => {
        if (handleType === 'source') {
          // Check source connection (output handles)
          if (edge.source === id) {
            // If sourceHandle is explicitly set, match it exactly
            if (edge.sourceHandle !== null && edge.sourceHandle !== undefined) {
              return edge.sourceHandle === handleId;
            }
            // If sourceHandle is null/undefined, it means the default handle
            // Match against common default handle names
            return handleId === 'main' || handleId === 'default' || handleId === 'output-0';
          }
        } else {
          // Check target connection (input handles)
          if (edge.target === id) {
            // If targetHandle is explicitly set, match it exactly
            if (edge.targetHandle !== null && edge.targetHandle !== undefined) {
              return edge.targetHandle === handleId;
            }
            // If targetHandle is null/undefined, it means the default handle
            return handleId === 'main' || handleId === 'default' || handleId === 'input-0';
          }
        }
        return false;
      });
      
      return connected;
    };
  }, [edges, id, nodeType]);
  
  // Helper function to calculate screen position of an anchor
  const getAnchorScreenPosition = (anchorOffsetX: number, anchorOffsetY: number) => {
    if (!nodeRef.current) return { x: 0, y: 0 };
    
    const nodeRect = nodeRef.current.getBoundingClientRect();
    return {
      x: nodeRect.left + anchorOffsetX,
      y: nodeRect.top + anchorOffsetY + 30, // Add offset to position menu below anchor
    };
  };
  
  // Debug logging for chat-memory-manager (commented out to reduce console noise)
  // if (nodeType === 'chat-memory-manager') {
  //   console.log('Chat Memory Manager Node:', {
  //     nodeType,
  //     fullDefinition,
  //     displayStyle: fullDefinition?.displayStyle,
  //   });
  // }
  
  // Get the display definition (colors, icons, etc.)
  const displayDef = getNodeDef(nodeType);
  
  // Check if this node is tool-capable (has tool capability)
  const isToolCapable = useMemo(() => {
    return fullDefinition?.toolCapable?.enabled || false;
  }, [fullDefinition]);
  
  // Check if this node is being used as a tool (connected to a 'tools' anchor)
  const isUsedAsTool = useMemo(() => {
    return edges.some(edge =>
      edge.target === id && edge.targetHandle === 'default' && edge.sourceHandle === 'tools'
    );
  }, [edges, id]);

  // Check if this node is being used as a poll source (connected to a 'pollSource' anchor)
  const isUsedAsPollSource = useMemo(() => {
    return edges.some(edge =>
      edge.target === id && edge.sourceHandle === 'pollSource'
    );
  }, [edges, id]);

  // Check if node is disabled
  const isDisabled = data.disabled || data.settings?.disabled || false;

  // Check if this node has a regular (non-tool) input connection (connected to left handle)
  const hasRegularInput = useMemo(() => {
    return edges.some(edge => 
      edge.target === id && edge.targetHandle !== 'default' && edge.sourceHandle !== 'tools'
    );
  }, [edges, id]);
  
  // Check if this node has a tool input connection (connected to top handle from tools)
  const hasToolInput = useMemo(() => {
    return edges.some(edge => 
      edge.target === id && edge.targetHandle === 'default' && edge.sourceHandle === 'tools'
    );
  }, [edges, id]);
  
  // Show as tool node ONLY if already connected as a tool
  const showAsToolNode = isUsedAsTool;

  // Show as poll source node if connected to a pollSource anchor
  const showAsPollSourceNode = isUsedAsPollSource;

  // Use appropriate color for connected sub-nodes
  const borderColor = showAsToolNode
    ? workflowColors.handle.tools
    : showAsPollSourceNode
    ? '#a855f7' // Purple for poll source
    : displayDef.color;
  
  // Get execution state from data
  const isExecuting = data.isExecuting || false;
  const executionStatus = data.executionStatus || 'idle';
  
  // Get inputs and outputs from the full definition
  const inputs = fullDefinition?.inputs || [{ name: 'default', type: 'main', displayName: 'Input', required: false }];
  const outputs = fullDefinition?.outputs || [{ name: 'default', type: 'main', displayName: 'Output' }];
  
  // Determine if this is a trigger node (no input)
  const isTrigger = displayDef.category === 'triggers';
  
  // Check if this is a sub-node
  const isSubNode = fullDefinition?.isSubNode || false;
  
  // Determine node size and shape based on type
  // AI Agent and nodes with displayStyle: 'wide-rectangular' get wider rectangular shape
  const isAIAgent = ['ai-agent', 'aiAgent', 'ai-batch', 'aiBatch'].includes(nodeType);

  // Check if this is a polling trigger (has pollSource anchor)
  const isPollingTrigger = ['polling-trigger', 'pollingTrigger'].includes(nodeType);
  const isWideRectangular = fullDefinition?.displayStyle === 'wide-rectangular' || isAIAgent;
  
  // Sub-nodes get circular shape, same size as triggers
  // Tool nodes (when used as tools) also get circular shape
  // Wide rectangular nodes get wider shape (AI Agent, Chat Memory Manager, etc.)
  // Tool-capable nodes (KB Search, Turso, etc.) should be standard size, not complex
  // Other complex nodes get standard larger size
  const isComplexNode = ['langchain-rag-chain'].includes(nodeType) ||
                        (displayDef.category === 'ai-nodes' && !isSubNode && !isWideRectangular && !isToolCapable);
  
  const nodeSize = isWideRectangular
    ? { width: 'w-54', height: 'h-28', icon: 'h-8 w-8', widthPx: 216, heightPx: 112, shape: 'rectangular' }  // Wide rectangular: 1.5x wider (216px)
    : (isSubNode || showAsToolNode || showAsPollSourceNode)
    ? { width: 'w-24', height: 'h-24', icon: 'h-8 w-8', widthPx: 96, heightPx: 96, shape: 'circular' }      // Sub-nodes, tool nodes, poll source nodes: circular
    : isComplexNode
    ? { width: 'w-32', height: 'h-32', icon: 'h-10 w-10', widthPx: 128, heightPx: 128, shape: 'rectangular' } // Complex: larger
    : { width: 'w-24', height: 'h-24', icon: 'h-8 w-8', widthPx: 96, heightPx: 96, shape: 'rectangular' };     // Standard
  
  // Calculate pixel positions for multiple handles based on card height
  // Returns pixel offset from top of wrapper (which starts at the card)
  const getHandlePositionPx = (index: number, total: number) => {
    const cardHeight = nodeSize.heightPx;
    if (total === 1) {
      return cardHeight / 2; // Center at 50% of card height
    }
    // Distribute handles evenly with 25% margins within the card
    const marginPercent = 0.25;
    const usableHeight = cardHeight * (1 - 2 * marginPercent); // 50% of card height
    const startOffset = cardHeight * marginPercent; // 25% from top
    const spacing = usableHeight / (total - 1);
    return startOffset + (spacing * index);
  };
  
  return (
    <NodeContextMenu nodeId={id}>
      <div
        ref={nodeRef}
        className="relative group flex flex-col items-center isolate"
      >
      {/* Input handles - only for non-trigger nodes */}
      {/* Sub-nodes always get a top handle, tool nodes get top handle, others get left handles */}
      {!isTrigger && (
        <>
          {/* Sub-nodes need a special input handle even if inputs array is empty */}
          {isSubNode && (
            <Handle
              key="subnode-input"
              id="subNodeInput"
              type="target"
              position={Position.Top}
              isConnectable={!isDisabled}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-125"
              style={{
                background: workflowColors.handle.default,
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '2px',
              }}
            />
          )}
          
          {/* Regular inputs for non-sub-nodes */}
          {!isSubNode && inputs.map((input, index) => {
            const inputName = input.name || `input-${index}`;

            // When dragging from tools/pollSource handle: HIDE left handle, SHOW top handle
            // When not dragging: show handles based on node state
            const isDraggingFromSubNodeHandle = isDraggingFromToolsHandle || isDraggingFromPollSourceHandle;
            const isConnectedAsSubNode = showAsToolNode || showAsPollSourceNode;
            const showLeftHandle = !isDraggingFromSubNodeHandle && !isConnectedAsSubNode;
            const showTopHandle = isDraggingFromSubNodeHandle || isConnectedAsSubNode;
            
            // Both handles are connectable when visible and node is not disabled
            const topHandleConnectable = showTopHandle && !isDisabled;
            const leftHandleConnectable = showLeftHandle && !isDisabled;
            

            
            return (
              <React.Fragment key={`input-${inputName}`}>
                {/* Left handle for regular connections - hidden when connected as sub-node */}
                {showLeftHandle && (
                  <Handle
                    id={inputName}
                    type="target"
                    position={Position.Left}
                    isConnectable={leftHandleConnectable}
                    className="w-5 h-5 border-2 border-white transition-all hover:scale-125"
                    style={{
                      background: workflowColors.handle.default,
                      top: `${getHandlePositionPx(index, inputs.length)}px`,
                      transform: 'translateY(-50%)',
                      left: '-10px',
                    }}
                  />
                )}

                {/* Top handle for sub-node connections (tools, pollSource) */}
                {showTopHandle && (
                  <Handle
                    id="subNodeInput"
                    type="target"
                    position={Position.Top}
                    isConnectable={topHandleConnectable}
                    className="w-5 h-5 border-2 border-white transition-all hover:scale-125"
                    style={{
                      background: workflowColors.handle.default,
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderRadius: '2px',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
      
      {/* Node Card - size and shape vary by type */}
      {/* z-10 ensures the card sits above the handles so they appear tucked underneath */}
      <div
        className={`${nodeSize.width} ${nodeSize.height} flex ${isWideRectangular ? 'flex-col' : ''} items-center justify-center ${
          nodeSize.shape === 'circular' ? 'rounded-full' : 'rounded-xl'
        } border-[3px] hover:shadow-lg transition-all duration-300 relative z-10 ${
          isDisabled ? 'opacity-60' : ''
        } cursor-pointer ${
          isExecuting ? 'animate-pulse ring-2 ring-accent-info-500 scale-110' : ''
        } ${
          executionStatus === 'success' ? 'ring-2 ring-accent-success-500' : ''
        } ${
          executionStatus === 'failed' ? 'ring-2 ring-destructive' : ''
        } ${
          isDropTarget ? 'ring-[6px] ring-primary scale-105' : ''
        }`}
        style={{
          background: isDisabled ? 'var(--muted)' : (isUsedAsTool ? 'var(--accent-success-50)' : (isUsedAsPollSource ? '#faf5ff' : displayDef.bgColor)),
          borderColor: isDisabled ? 'var(--border)' : borderColor,
          boxShadow: isDropTarget
            ? '0 0 20px 8px rgba(79, 70, 229, 0.5), 0 0 40px 16px rgba(79, 70, 229, 0.25)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderRadius: isTrigger ? '40%' : undefined,
        }}
      >
        <NodeIcon
          icon={displayDef.icon}
          iconType={displayDef.iconType}
          iconColor={showAsToolNode ? workflowColors.handle.tools : showAsPollSourceNode ? '#a855f7' : displayDef.iconColor}
          className={`${nodeSize.icon} ${isExecuting ? 'animate-spin' : ''} ${isWideRectangular ? 'mt-2' : ''}`}
          color={showAsToolNode ? workflowColors.handle.tools : showAsPollSourceNode ? '#a855f7' : displayDef.color}
        />
        
        {/* Wide rectangular nodes get label inside node */}
        {isWideRectangular && (
          <div className="text-xs font-medium text-foreground mt-1 px-2 text-center truncate max-w-full">
            {data.label || displayDef.label}
          </div>
        )}

        {/* Disabled badge */}
        {isDisabled && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-muted-foreground rounded-full flex items-center justify-center">
            <PowerOff className="w-3 h-3 text-white" />
          </div>
        )}
        
        {/* Execution status indicator - hide when disabled */}
        {!isDisabled && isExecuting && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent-info-500 rounded-full animate-ping" />
        )}
        {!isDisabled && executionStatus === 'success' && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent-success-500 rounded-full" />
        )}
        {!isDisabled && executionStatus === 'failed' && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full" />
        )}

        {/* AI Agent Anchor Labels - rendered inside card so they're visible */}
        {isAIAgent && (
          <>
            <div className="absolute left-[15%] bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#a855f7] whitespace-nowrap pointer-events-none">
              Model
            </div>
            <div className="absolute left-[38%] bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#3b82f6] whitespace-nowrap pointer-events-none">
              Memory
            </div>
            <div className="absolute left-[62%] bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#10b981] whitespace-nowrap pointer-events-none">
              Tools
            </div>
            <div className="absolute left-[85%] bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#f97316] whitespace-nowrap pointer-events-none">
              Parser
            </div>
          </>
        )}

        {/* Polling Trigger Label - rendered inside card */}
        {isPollingTrigger && (
          <div className="absolute left-1/2 bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#a855f7] whitespace-nowrap pointer-events-none">
            Source
          </div>
        )}

        {/* Parser Sub-Node Label - rendered inside card */}
        {fullDefinition?.isSubNode &&
         fullDefinition?.subNodeConfig?.category === 'parser' &&
         fullDefinition?.anchors?.input &&
         fullDefinition.anchors.input.length > 0 && (
          <div className="absolute left-1/2 bottom-1 -translate-x-1/2 text-[10px] font-medium text-[#f97316] whitespace-nowrap pointer-events-none">
            Model
          </div>
        )}
      </div>

      {/* Chat Trigger Test Button - appears on hover to the left of the node */}
      {nodeType === 'chat-trigger' && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 opacity-0 group-hover:opacity-100 transition-opacity w-8 h-8 flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md z-20"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChatTest?.(id);
          }}
          title="Test Chat Workflow"
        >
          <NodeIcon 
            icon={displayDef.icon}
            iconType={displayDef.iconType}
            iconColor={displayDef.iconColor}
            className="w-4 h-4"
          />
        </button>
      )}
      
      {/* Chat Memory Manager Anchor Point - single memory anchor centered */}
      {nodeType === 'chat-memory-manager' && (
        <div className="absolute bottom-0 left-[50%] translate-y-1/2 -translate-x-1/2 group/anchor z-10">
          {/* Memory Label - Above anchor, centered - Always visible */}
          <div className="absolute bottom-full mb-0 left-1/2 -translate-x-[80%] text-[10px] font-medium text-[#3b82f6] whitespace-nowrap pointer-events-none">
            Memory
          </div>

          {!isAnchorConnected('memory') ? (
            <>
              <Handle
                id="memory"
                type="source"
                position={Position.Bottom}
                isConnectable={!isDisabled}
                className="w-5 h-5 border-2 border-white transition-all hover:scale-125 cursor-crosshair"
                style={{
                  background: workflowColors.handle.memory,
                  borderRadius: '50%',
                  position: 'relative',
                  left: 0,
                  top: 0,
                }}
              />
              {/* Plus button - positioned beneath handle with minimal overlap */}
              <button
                className="absolute top-4 left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
                onClick={(e) => {
                  e.stopPropagation();
                  const position = getAnchorScreenPosition(nodeSize.widthPx / 2, nodeSize.heightPx);
                  onAddNodeClick?.(id, 'memory', 'memory', position);
                }}
                title="Add memory"
                disabled={isDisabled}
              >
                <span className="text-[#3b82f6] text-base font-bold leading-none">+</span>
              </button>
            </>
          ) : (
            <Handle
              id="memory"
              type="source"
              position={Position.Bottom}
              isConnectable={false}
              className="w-5 h-5 border-2 border-white"
              style={{
                background: workflowColors.handle.memory,
                borderRadius: '50%',
                position: 'relative',
                left: 0,
                top: 0,
              }}
            />
          )}
        </div>
      )}
      
      {/* AI Agent Anchor Points - half-moon style, labels inside node, positioned using top based on node height */}
      {isAIAgent && (
        <>
          {/* Chat Model Anchor - Left (15%) */}
          <div className="absolute left-[15%] -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
            <Handle
              id="chatModel"
              type="source"
              position={Position.Bottom}
              isConnectable={!isDisabled && !isAnchorConnected('chatModel')}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
              style={{
                background: workflowColors.handle.chatModel,
                top: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
              }}
            />
            {/* Plus button - appears on hover below handle */}
            {!isAnchorConnected('chatModel') && (
              <button
                className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
                style={{ top: '26px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const position = getAnchorScreenPosition(nodeSize.widthPx * 0.15, nodeSize.heightPx);
                  onAddNodeClick?.(id, 'chatModel', 'chatModel', position);
                }}
                title="Add chat model"
                disabled={isDisabled}
              >
                <span className="text-[#a855f7] text-base font-bold leading-none">+</span>
              </button>
            )}
          </div>

          {/* Memory Anchor - (38%) */}
          <div className="absolute left-[38%] -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
            <Handle
              id="memory"
              type="source"
              position={Position.Bottom}
              isConnectable={!isDisabled && !isAnchorConnected('memory')}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
              style={{
                background: workflowColors.handle.memory,
                top: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
              }}
            />
            {!isAnchorConnected('memory') && (
              <button
                className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
                style={{ top: '26px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const position = getAnchorScreenPosition(nodeSize.widthPx * 0.38, nodeSize.heightPx);
                  onAddNodeClick?.(id, 'memory', 'memory', position);
                }}
                title="Add memory"
                disabled={isDisabled}
              >
                <span className="text-[#3b82f6] text-base font-bold leading-none">+</span>
              </button>
            )}
          </div>

          {/* Tools Anchor - (62%) */}
          <div className="absolute left-[62%] -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
            <Handle
              id="tools"
              type="source"
              position={Position.Bottom}
              isConnectable={!isDisabled}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
              style={{
                background: workflowColors.handle.tools,
                top: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
              }}
            />
            <button
              className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
              style={{ top: '26px' }}
              onClick={(e) => {
                e.stopPropagation();
                const position = getAnchorScreenPosition(nodeSize.widthPx * 0.62, nodeSize.heightPx);
                onAddNodeClick?.(id, 'tools', 'tools', position);
              }}
              title="Add tool"
              disabled={isDisabled}
            >
              <span className="text-[#10b981] text-base font-bold leading-none">+</span>
            </button>
          </div>

          {/* Parser Anchor - (85%) */}
          <div className="absolute left-[85%] -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
            <Handle
              id="parser"
              type="source"
              position={Position.Bottom}
              isConnectable={!isDisabled && !isAnchorConnected('parser')}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
              style={{
                background: workflowColors.handle.parser,
                top: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
              }}
            />
            {!isAnchorConnected('parser') && (
              <button
                className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
                style={{ top: '26px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const position = getAnchorScreenPosition(nodeSize.widthPx * 0.85, nodeSize.heightPx);
                  onAddNodeClick?.(id, 'parser', 'parser', position);
                }}
                title="Add parser"
                disabled={isDisabled}
              >
                <span className="text-[#f97316] text-base font-bold leading-none">+</span>
              </button>
            )}
          </div>
        </>
      )}

      {/* Polling Trigger Data Source Anchor - half-moon style, label inside node */}
      {isPollingTrigger && (
        <div className="absolute left-1/2 -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
          <Handle
            id="pollSource"
            type="source"
            position={Position.Bottom}
            isConnectable={!isDisabled && !isAnchorConnected('pollSource')}
            className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
            style={{
              background: '#a855f7',
              top: '0px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 5,
            }}
          />
          {/* Plus button - appears on hover below handle */}
          {!isAnchorConnected('pollSource') && (
            <button
              className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
              style={{ top: '26px' }}
              onClick={(e) => {
                e.stopPropagation();
                const position = getAnchorScreenPosition(nodeSize.widthPx / 2, nodeSize.heightPx);
                onAddNodeClick?.(id, 'pollSource', 'pollSource', position);
              }}
              title="Add data source node"
              disabled={isDisabled}
            >
              <span className="text-[#a855f7] text-base font-bold leading-none">+</span>
            </button>
          )}
        </div>
      )}


      {/* Label underneath the node - hide for wide rectangular nodes (label is inside) */}
      {/* IMPORTANT: Label width must match node card width to keep handles aligned */}
      {!isWideRectangular && (
        <div className={`mt-2 ${isComplexNode ? 'w-32' : 'w-24'}`}>
          <div className="text-xs font-medium text-foreground text-center line-clamp-2">
            {data.label || displayDef.label}
          </div>
        </div>
      )}
      
      {/* Parser Sub-Node Anchor - half-moon style for parser nodes (like Agent Output Parser) */}
      {fullDefinition?.isSubNode &&
       fullDefinition?.subNodeConfig?.category === 'parser' &&
       fullDefinition?.anchors?.input &&
       fullDefinition.anchors.input.length > 0 && (
        <>
          {fullDefinition.anchors.input.map((anchor, index) => {
            const anchorId = anchor.subNodeCategory || `anchor-${index}`;
            const anchorLabel = anchor.label || anchorId;
            const anchorColor = anchor.subNodeCategory === 'chatModel' ? workflowColors.handle.chatModel : workflowColors.handle.default;

            return (
              <div key={`sub-anchor-${anchorId}`} className="absolute left-1/2 -translate-x-1/2 group/anchor" style={{ top: `${nodeSize.heightPx - 10}px` }}>
                <Handle
                  id={anchorId}
                  type="source"
                  position={Position.Bottom}
                  isConnectable={!isDisabled && !isAnchorConnected(anchorId)}
                  className="w-5 h-5 border-2 border-white transition-all hover:scale-110 cursor-crosshair"
                  style={{
                    background: anchorColor,
                    top: '0px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 5,
                  }}
                />
                {/* Plus button - appears on hover below handle */}
                {!isAnchorConnected(anchorId) && (
                  <button
                    className="absolute left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover/anchor:opacity-100 transition-opacity pointer-events-auto cursor-pointer hover:bg-white shadow-md z-20"
                    style={{ top: '26px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const position = getAnchorScreenPosition(nodeSize.widthPx / 2, nodeSize.heightPx);
                      onAddNodeClick?.(id, anchorId, anchor.subNodeCategory, position);
                    }}
                    title={`Add ${anchorLabel}`}
                  >
                    <span className="text-base font-bold leading-none" style={{ color: anchorColor }}>+</span>
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}
      
      {/* Output handles with labels - hide for tool nodes */}
      {/* Allows multiple connections (fan-out) from the same output handle */}
      {!isUsedAsTool && !isUsedAsPollSource && outputs.map((output, index) => {
        const topPositionPx = getHandlePositionPx(index, outputs.length);
        const showLabel = outputs.length > 1; // Only show labels when there are multiple outputs
        const handleId = output.name || `output-${index}`;

        return (
          <div
            key={`output-${output.name || index}`}
            className="absolute right-0 group/anchor"
            style={{
              top: `${topPositionPx}px`,
            }}
          >
            <Handle
              id={handleId}
              type="source"
              position={Position.Right}
              isConnectable={!isDisabled}
              isConnectableStart={true}
              className="w-5 h-5 border-2 border-white transition-all hover:scale-125 cursor-crosshair"
              style={{
                background: workflowColors.handle.default,
                top: 0,
                transform: 'translateY(-50%)',
                right: '-10px',
              }}
            />

            {/* Plus button - positioned to the right of handle, appears on hover */}
            <button
              className="absolute left-3 top-0 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground opacity-0 group-hover/anchor:opacity-100 shadow-lg hover:shadow-xl transition-all hover:scale-110 pointer-events-auto cursor-pointer z-20"
              onClick={(e) => {
                e.stopPropagation();
                // Calculate screen position for the palette
                if (nodeRef.current) {
                  const nodeRect = nodeRef.current.getBoundingClientRect();
                  const handleY = nodeRect.top + topPositionPx;
                  const position = {
                    x: nodeRect.right + 30, // Position to the right of the node
                    y: handleY,
                  };
                  onAddNodeClick?.(id, handleId, undefined, position);
                }
              }}
              title="Add node"
              disabled={isDisabled}
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
            </button>

            {showLabel && output.displayName && (
              <div
                className="absolute text-[10px] font-medium text-muted-foreground whitespace-nowrap pointer-events-none select-none bg-background/80 px-1 rounded"
                style={{
                  left: 16, // Position to the right of the handle
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                {output.displayName}
              </div>
            )}
          </div>
        );
      })}
      </div>
    </NodeContextMenu>
  );
}
